// src/components/UpgradeLock.jsx
// Inline "not on your plan" notice, used in place of a locked action/section.
import { useState } from "react";
import { Lock } from "lucide-react";
import apiService from "../services/api";

export const UPGRADE_LOCK_STYLES = `
  .upg-lock { display: flex; align-items: flex-start; gap: 10px; padding: 12px 14px; border: 1px dashed var(--border-strong); border-radius: var(--radius-sm); background: var(--surface-raised); color: var(--text-muted); font-size: var(--text-sm); line-height: 1.45; }
  .upg-lock svg { flex-shrink: 0; margin-top: 2px; color: var(--text-faint); }
  .upg-lock strong { color: var(--text); font-weight: 600; }
  .upg-panel { max-width: 520px; margin: 64px auto; padding: 32px; text-align: center; background: var(--surface-raised); border: 1px solid var(--border); border-radius: var(--radius-lg); }
  .upg-panel-icon { width: 44px; height: 44px; margin: 0 auto 16px; display: flex; align-items: center; justify-content: center; border-radius: 50%; background: var(--surface-sunken); color: var(--text-muted); }
  .upg-panel h2 { margin: 0 0 8px; font-size: var(--text-md); font-weight: 600; color: var(--text); }
  .upg-panel p { margin: 0 0 20px; font-size: var(--text-sm); line-height: 1.5; color: var(--text-muted); }
  .upg-panel-actions { display: flex; gap: 8px; justify-content: center; flex-wrap: wrap; }
  .upg-panel-status { margin-top: 16px; font-size: var(--text-sm); color: var(--text-muted); }
  .upg-lock.compact { padding: 8px 10px; font-size: var(--text-xs); }
`;

/**
 * Full-section notice for a locked area (e.g. the Sales tab). Billing is
 * manual, so "Request upgrade" notifies support rather than starting a
 * checkout.
 */
export function UpgradePanel({ feature, plans, onBack }) {
  const [state, setState] = useState("idle"); // idle | sending | sent | error
  const request = async () => {
    setState("sending");
    try {
      await apiService.requestPlanUpgrade({ feature });
      setState("sent");
    } catch (err) {
      console.error("Upgrade request failed:", err);
      setState("error");
    }
  };
  return (
    <div className="upg-panel" role="region" aria-label={`${feature} upgrade`}>
      <div className="upg-panel-icon">
        <Lock size={20} aria-hidden="true" />
      </div>
      <h2>{feature} is not on your plan</h2>
      <p>This feature is only available for the {plans} plans.</p>
      <div className="upg-panel-actions">
        {onBack && (
          <button
            type="button"
            className="btn btn-secondary btn-md"
            onClick={onBack}
          >
            Back to Overview
          </button>
        )}
        <button
          type="button"
          className="btn btn-primary btn-md"
          disabled={state === "sending" || state === "sent"}
          onClick={request}
        >
          {state === "sending"
            ? "Sending…"
            : state === "sent"
              ? "Request sent"
              : "Upgrade plan"}
        </button>
      </div>
      {state === "sent" && (
        <div className="upg-panel-status" role="status">
          Thanks. Our team will contact you shortly to complete the upgrade.
        </div>
      )}
      {state === "error" && (
        <div className="upg-panel-status" role="alert">
          Couldn't send your request. Please try again.
        </div>
      )}
    </div>
  );
}

export default function UpgradeLock({ feature, planLabel, compact = false }) {
  return (
    <div className={`upg-lock${compact ? " compact" : ""}`} role="note">
      <Lock size={compact ? 12 : 14} aria-hidden="true" />
      <span>
        <strong>{feature}</strong> is available on the {planLabel} plan and
        above.
      </span>
    </div>
  );
}
